import { Fragment } from 'react'
import { range } from 'lodash'

import { Text, Link, Accordion, Separator } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'

import { useReselector } from '@/utils/sharedHooks'
import { selectSortedTaxReturnDocumentsForYear } from '@/features/user/your-practice/documents/lib/userDocuments.selector'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { AnnualTaxFaq } from '../AnnualTaxFaq'
import { PageHeader } from '@/components/PageHeader'

// TODO: Migrate to V2

import { selectCurrentAnnualTaxYear } from '@/featuresV1/Admin/AnnualTaxDetails/annualTaxDetails.selector'

const AnnualTaxFilingFormTaxDocument = ({
  document,
}: {
  document: UserDocument
}) =>
  document.signedUrl ? (
    <div className="w-full md:w-1/2">
      <Link href={document.signedUrl} newPage variant="secondary">
        {document.fileDescription}
      </Link>
    </div>
  ) : null

const ReturnSection = ({
  title,
  documents,
}: {
  title: string
  documents: UserDocument[]
}) =>
  documents.length ? (
    <>
      <div className="w-full">
        <Text variant={TypographyStyles.EYEBROW}>{title}</Text>
      </div>
      {documents.map((doc) => (
        <AnnualTaxFilingFormTaxDocument key={doc.id} document={doc} />
      ))}
    </>
  ) : null

const ReturnYear = ({ year }: { year: number }) => {
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)

  const businessTaxReturnsForYear = useReselector(
    selectSortedTaxReturnDocumentsForYear,
    year.toString()
  )?.business

  const personalTaxReturnsForYear = useReselector(
    selectSortedTaxReturnDocumentsForYear,
    year.toString()
  )?.personal

  const otherTaxReturnsForYear =
    useReselector(selectSortedTaxReturnDocumentsForYear, year.toString())
      ?.other || []

  if (
    !businessTaxReturnsForYear.length &&
    !personalTaxReturnsForYear.length &&
    !otherTaxReturnsForYear.length
  ) {
    return (
      <div className="w-full">
        <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
          {currentTaxYear === year.toString() ? (
            <>
              When you are finished filing taxes with us, a copy of your returns
              will show here.
              <br />
              <br />
              If you&apos;ve already completed your taxes, but you don&apos;t
              see your returns here, please upload them in the Tax Documents
              section of the Documents page.
            </>
          ) : (
            <>
              It looks like we don’t have your {year} tax returns. You can
              upload your return via the Tax Documents section of the Documents
              page.
            </>
          )}{' '}
          <Link href="/practice/documents?tab=tax" variant="primary">
            Go to Documents
          </Link>
        </Text>
      </div>
    )
  }

  return (
    <>
      <ReturnSection title="business" documents={businessTaxReturnsForYear} />
      <ReturnSection title="personal" documents={personalTaxReturnsForYear} />
      <ReturnSection title="other" documents={otherTaxReturnsForYear} />
    </>
  )
}

export const PastReturnsPanel = () => {
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)

  return (
    <div className="flex flex-col gap-6">
      <PageHeader header="Tax Return Archive" />
      <div className="flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-2/3 space-y-6">
          <Text variant={TypographyStyles.BODY_SM}>
            Not seeing one of your returns? You can upload them in the Tax
            Documents section of the Documents page.{' '}
            <Link href="/practice/documents?tab=tax">Go to Documents</Link>
          </Text>

          {range(Number(currentTaxYear), 2020, -1).map((year) => (
            <Fragment key={year}>
              <Accordion
                type="multiple"
                variant="header"
                items={[
                  {
                    title: `${year} Tax Returns`,
                    content: <ReturnYear year={year} />,
                  },
                ]}
              />
              <Separator />
            </Fragment>
          ))}
        </div>
        <div className="w-full md:w-1/3">
          <AnnualTaxFaq />
        </div>
      </div>
    </div>
  )
}
